/**
 * Canonical exercise identity.
 *
 * Weight history is keyed by a *canonical exercise id*, not the free-text name,
 * so the same movement typed differently across programs/uploads still shares
 * one history. `canonicalExerciseId('Back Squat')`, `'back squat'`,
 * `'Backsquat'` and `'BB Back Squat'` all resolve to `'back-squat'`.
 *
 * Two layers:
 *  1. slugify — handles casing, spacing, punctuation (covers the common case).
 *  2. ALIASES — maps known synonyms/abbreviations onto a canonical slug.
 *
 * The library table (public.exercises) can extend ALIASES at runtime later; for
 * now this deterministic function keeps client and server in agreement.
 */

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Common equipment prefixes that don't change the movement's identity.
const STRIP_PREFIXES = ['bb-', 'db-', 'barbell-', 'dumbbell-', 'kb-', 'kettlebell-']

// Explicit synonyms → canonical slug.
const ALIASES: Record<string, string> = {
  'back-squat': 'back-squat',
  'backsquat': 'back-squat',
  'rdl': 'romanian-deadlift',
  'romanian-dl': 'romanian-deadlift',
  'sldl': 'romanian-deadlift',
  'bench': 'bench-press',
  'db-bench': 'db-bench-press',
  'ohp': 'overhead-press',
  'chin-ups': 'chin-up',
  'chinup': 'chin-up',
  'pull-ups': 'pull-up',
  'pullup': 'pull-up',
}

export function canonicalExerciseId(name: string): string {
  let slug = slugify(name)
  if (ALIASES[slug]) return ALIASES[slug]
  for (const p of STRIP_PREFIXES) {
    if (slug.startsWith(p)) {
      const stripped = slug.slice(p.length)
      if (ALIASES[stripped]) return ALIASES[stripped]
      slug = stripped
      break
    }
  }
  return ALIASES[slug] ?? slug
}

/**
 * Resolve the id an exercise should log against. Prefer an explicit library id
 * on the exercise; otherwise derive it canonically from the name.
 */
export function resolveExerciseId(exercise: { exercise_id?: string | null; name: string }): string {
  const explicit = exercise.exercise_id?.trim()
  if (explicit && explicit !== 'warmup' && explicit !== 'warm-up') {
    // If a program author used a raw slug, still canonicalise obvious variants.
    return ALIASES[explicit] ?? explicit
  }
  return canonicalExerciseId(exercise.name)
}
